/** The hero sheet's stylesheet, and the geometry it is cut to.
 *
 * Split out of `HeroScreen.tsx` on 2026-09-17 unchanged. The constants live
 * beside the sheet because the sheet interpolates them and the component
 * measures with them; a doll cell typed twice is a doll that does not fit.
 * `HeroScreen.tsx` re-exports `HERO_CSS`, `HERO_GEOMETRY`, `BUTTONS_W` and
 * `BUTTONS_W_COMPACT` so the tests that hold them did not move.
 */

import { measureBitmapText } from "../theme/bitmapText.js";
import { WOOD_FIBRE, WOOD_FIGURE, reliefDecls } from "../theme/relief.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/* Every one of these is an exact divisor of its 128px or 256px source, at both
   sizes: a sprite drawn at 3/5 of its frame is mush, and the sheet is mostly
   sprites. `compact` is the 1366x768 set - the sheet has to hold a 19-socket
   doll, a pack, eight skills and an army in 768 rows, and something has to give;
   what gives is sprite size, never a panel's contents. */
/* The doll's socket, and the whole of the argument for its size.
 *
 * The classic's plate: nineteen 44px sockets in a ~291x345 leather field, so 36.6% of
 * the plate is socket and the other 63% is leather with a figure drawn on it.
 * Ours shipped as nine 132px sockets in 484x480 - 67.5% socket, no figure -
 * which is why it read as a grid of holes rather than as a person.
 *
 * This was cited to a reference screenshot, which does
 * not exist. **Re-measured 2026-08-30 and it holds**, on
 * A reference screenshot - a genuine the classic hero screen (a ranger, Level
 * 18 Ranger) from the classic gallery for *the classic: Complete*, 710x533, a downscale
 * of a native 800x600 frame. Read there and rescaled to 800x600: the doll plate
 * is 287x344, an empty socket is 43x42, and there are nineteen of them, so
 * 19 x 44^2 / (291 x 345) = 36.6% stands to within the frame's own resolution.
 * The gaps are leather with a green figure silhouette painted through them,
 * exactly as described.
 *
 * 76 with a 26px gutter puts five columns and six rows in the 484x586 the gear
 * column has: 19 x 76^2 / (484 x 586) = 38.7%, the reference's proportion to
 * within two points, with enough leather between the cuts for the figure
 * underneath to be seen through the gaps.
 *
 * ## What that costs, because it is a real cost
 *
 * An artifact frame is 128px and `fitSpriteBox` may only divide by a whole
 * number, so a socket below 128 draws the art at 64 and there is nothing in
 * between. The nine worn artifacts were drawn at 128 and are now drawn at 64.
 *
 * Keeping 128 AND a five-column lattice needs 5 x 132 + gutters = 764px of
 * lattice, so a 820px gear column, and at 1920x1080 the main grid has 1532px
 * to spend on skills, the spell index and the gear together. 820 leaves the
 * eight skill plates 139px each, which is narrower than one skill icon plus a
 * word. The spell index will not move to the left column either: the left
 * column is 1022px tall and the portrait, primaries, fortune, experience and
 * reserves already ask for 815 of it, against the index's 335. So the two are
 * mutually exclusive at this resolution, and the composition is the thing the
 * blind test kept naming. Written up in the report.
 */
export const DOLL_CELL = 76;
export const DOLL_GAP = 26;
export const DOLL_CELL_COMPACT = 64;
export const DOLL_GAP_X_COMPACT = 6;
export const DOLL_GAP_Y_COMPACT = 12;
/* Every socket on the plate asks `fitSpriteBox` for its own square, and the
   divisor falls out of the art: an artifact frame is 128 and halves to 64 in
   both sizes; a war machine is 192x211 of battlefield art and thirds to 64x70
   at 1080p, quarters to 48x52 at 1366x768. One size for one plate - the rack
   with its own oblong pockets existed only because the machines shared a
   64px pocket with the miscellaneous artifacts, and they no longer do. */
export const SHELF_ICON_COMPACT = 32;
/* Secondary-skill art is 96px at source, so both of these are scale 1 and 1/2. */
export const SKILL_ICON = 96;
export const SKILL_ICON_COMPACT = 48;
/* Primary-skill art is 96px at source; a half and a third of it. The reference
   fills its stat cell with a ~46px painted device (crossed swords, shield,
   book, bookshelf: REF_darkstorm_stats_2x) and stamps the figure on a narrow
   bar under it; a 32px stamp in a 140px cell read as a placeholder chip. */
export const PRIM_ICON = 48;
export const PRIM_ICON_COMPACT = 32;

/* The two plates at the army band's right end are sized from their own
   labels, not from a number that happened to fit at 1920. At 1366 the
   96px column was 150px of SPELLBOOK glyphs wide, and the S and the K sat on
   the bezel. The compact plate sets its label in the text face (cap 9), the
   roomy one in the display face (cap 12); each column is the wider label
   plus the plate's padding and rail. */
export const BUTTON_LABELS = ["SPELLBOOK", "CLOSE"] as const;
export const labelWidth = (face: "serif" | "serifDisplay"): number =>
  Math.max(...BUTTON_LABELS.map((t) => measureBitmapText(t, { face }).width));
/** Column width, roomy: label + 2 x (9px pad + 1px rail) + 6px of ease. */
export const BUTTONS_W = labelWidth("serifDisplay") + 2 * 10 + 6;
/** Column width, compact: label + 2 x (5px pad + 1px rail) + 4px of ease. */
export const BUTTONS_W_COMPACT = labelWidth("serif") + 2 * 6 + 4;
/* The two live resources get a plaque of their own, so their icons are drawn at
   half of the 96px source rather than at a third. */
/* The mood faces are 96px art like every other `ui.*` icon, so a half. */
export const MOOD_ICON_SIZE = 48;
export const DIAL_ICON = 48;
export const PORTRAIT_COMPACT = 128;
/* The height the army divisor is chosen against, not the socket. See the header
   note: it is measured on the untrimmed frame so every stack shares a scale. */
export const ARMY_GROUND = 150;
export const ARMY_GROUND_COMPACT = 96;
/* The plinth under a stack, and the reason the count plaque no longer sits on
   the creature's feet.
 *
 * The plaque used to be `position: absolute; right: 0; bottom: 0` over the
 * sprite, which put it in the one place a standing figure is guaranteed to
 * have ink. It is now set into a band across the foot of the socket whose
 * height is exactly the gap the ground line already left below the feet, so
 * the plaque costs the sprite nothing: the divisor, and therefore every
 * stack's drawn size, is unchanged.
 *
 * That is also the ceiling on how big the numeral may be. A bitmap glyph is 7
 * rows plus a 1px keyline, so scale 2 is 18x14 of ink and scale 3 is 27x21 -
 * and 21 plus the plaque's own inset does not fit in 18 rows. Raising the
 * band instead would push the ground line to 142 or less, where `ceil(287/g)`
 * steps from 2 to 3 and every Lancer on the sheet loses a third of its
 * height. A bigger numeral is not worth a smaller army. */
export const ARMY_PLINTH = 21;
export const ARMY_PLINTH_COMPACT = 13;
/* The count numeral, at a whole magnification and with no box around it.
 *
 * This is the classic's own answer. It used to be cited to a 1920x1080 HD hero screen
 * (`refs/hero/h3hd-hero-darkstorn-lvl5-warlock`) which does not exist; the
 * absolute figures quoted from it - "27px of ink in a 118x130 socket" - are
 * unverifiable and are dropped.
 *
 * The claim itself re-measures on a reference screenshot, a genuine
 * hero screen that IS on disk. The army rack there is SEVEN sockets in one row
 * (bevel centres at x = 62, 121, 180, 238, 297, 356, 415, 474 in the frame's own
 * 710px width, a pitch of 58.8 = 66.3 at native 800x600), each holding a
 * cropped landscape creature portrait, and the stack count is white serif digits
 * with a black keyline drawn straight onto that portrait at its bottom right -
 * no plaque, no plate, no box. Measured on the "16": 11 rows of ink in a 57-row
 * socket interior, **19.3% of the socket's height**, not the fifth-ish 13-14%
 * the missing files were said to give. Ours shipped as 9x7 of bitmap ink inside
 * a 44px plaque: 20% of its own width, the smallest numeral on a sheet where it
 * is one of the three numbers a player actually reads.
 *
 * Dropping the box is what buys the size. A boxed numeral has to fit in the
 * 21-row band below the ground line or it sits on the creature's feet; an
 * outlined one is *meant* to sit on the creature, and `BitmapText` already
 * dilates a full 8-neighbour keyline around every glyph precisely so it stays
 * legible over a bright sprite and over near-black wood. Scale 4 puts 28px of
 * ink under a 122px Angel, which is the reference's proportion to within a
 * pixel. What binds now is WIDTH, not height, and `hero-plate.spec.ts` holds
 * the widest string `compactCount` can produce against the socket. */
export const ARMY_COUNT_SCALE = 4;
export const ARMY_COUNT_SCALE_COMPACT = 2;
/* Socket widths, measured in the browser at both viewports (2026-09-02, via
   getBoundingClientRect on `.hm-hs__army`: 135.7 and 90.6): the army row is
   seven `minmax(0, 1fr)` columns of the band beside the button column, so
   this is the width the grid gives, not a width this file sets. Kept here for
   the width test. */
export const ARMY_SOCKET_W = 135;
export const ARMY_SOCKET_W_COMPACT = 90;

/* The rules text on a plaque, in whole lines.
 *
 * The note box is handed the card's leftover height rather than asking for its
 * own (see `.hm-hs__skillnote`), so the only thing left to decide is where the
 * cut lands. `-webkit-line-clamp` puts it on a line boundary with an ellipsis
 * instead of through the middle of a row of glyphs, and the clamp is computed
 * from the measured box: a constant clamp is a guess at a height the grid
 * decides, and the guess was 4 where a 640px-tall window has room for 2.
 *
 * The cap is 4. It was 3 while the plaque's foot was bare, on the argument
 * that the fourth line only bought a clause - but the foot was 42px of blank
 * plate on a 168px card at 1920x1080 and 44px of 116 at 1366x768, so the line
 * that "was not worth it" was being traded for nothing. With the mastery rail
 * anchoring the foot the leftover is the note's again, and the one description
 * that needs the fourth line - Diplomacy, which otherwise ends "costs a
 * quarter of the usual..." - now reads to its full stop. The measured floor
 * still bites first wherever the card is short. */
export const SKILL_NOTE_LINE_EM = 1.4;
export const SKILL_NOTE_FONT = 12.5;
export const SKILL_NOTE_FONT_COMPACT = 11;
export const SKILL_NOTE_MAX_LINES = 4;

/* The spell index's name at 1366x768, and the two lines it is allowed. Both
   are here rather than inline in the stylesheet because the whole argument for
   wrapping is that two lines of this size fit inside the row's own height,
   which the 32px icon sets - see the compact rule for the measurement. */
export const SPELL_NAME_FONT_COMPACT = 11;
export const SPELL_NAME_LINE_COMPACT = 1.15;
export const SPELL_NAME_MAX_LINES_COMPACT = 2;

/** The three rungs of a secondary skill, in order, for the mastery rail. */
export const MASTERY_RAIL: readonly string[] = Object.freeze(["Basic", "Advanced", "Expert"]);

// ------------------------------------------------------------------ styles

export const HERO_STYLE_ID = "hm-heroscreen-art";

/* There is no figure behind the doll, and that is the decision, not an omission.
 *
 * What was here was a 1-2px warm-grey stroke (#B8A687) at 62% over a 16%-alpha
 * fill, tracing a head octagon, shoulders, arms, hips and two foot stubs. At
 * 1:1 the fill did not register at all, so what a player saw was a hairline
 * armature: a drawing whose stroke out-ranked every real element in the panel,
 * which crossed the socket boxes, and whose two unclosed trapezoids below the
 * boots socket read as a further pair of empty sockets.
 *
 * A filled, shaded figure was drawn in its place and measured, and it does not
 * work at this geometry. Nine 132px sockets in a 484x536 plate leave 30px
 * vertical and 26px horizontal gutters, and a body is only ever seen through
 * those strips: the helm becomes a tab above the head socket, the torso a bar
 * between two rows, the legs two blocks under the boots. The socket size is not
 * negotiable either - the artifact frames are 128px and a smaller socket forces
 * `fitSprite` onto the next whole divisor, which halves every icon on the sheet.
 *
 * So the plate is a leather board with nine pockets cut into it, and it says
 * only true things. A figure here needs the sockets to be small relative to the
 * body, which is a re-proportioning of the whole gear column rather than an
 * etch. See the report.
 */

/**
 * The backpack shelf's nominal geometry, in one place because two things need
 * it: the stylesheet below, and `packShelf`, which divides the card's measured
 * height by it. A pocket is square, so the cell is its side. Split out rather
 * than typed twice - a shelf whose stylesheet and whose arithmetic disagree
 * draws a row that does not fit, or leaves one it could have filled. The cell
 * is nominal because `packShelf` may shrink it, never past the floor below, to
 * buy the row the remainder would otherwise waste.
 */
export const PACK_COLS = 5;
/** After a wheel turn of the pack, further wheel events this long are one flick. */
export const PACK_WHEEL_DEAF_MS = 180;
/** The desktop pager column beside the strip: wide enough for "11-15" over "of 18". */
export const PACK_PAGER_W = 84;
export const PACK_PAGER_W_COMPACT = 64;
export const PACK_CELL = 72;
export const PACK_GAP = 6;
export const PACK_CELL_COMPACT = 44;
export const PACK_GAP_COMPACT = 4;
/* How small a pocket may be shrunk to claim one more row; see `packShelf`.
   Below these a recess stops reading as a socket and starts reading as a tile. */
export const PACK_CELL_MIN = 60;
export const PACK_CELL_MIN_COMPACT = 38;

/**
 * The numbers the army band and the war-machine rack are built from, in one
 * place so a test can hold them against the shipped atlas.
 *
 * Three of these are load-bearing against art this file does not own:
 * `armyGround` decides every stack's divisor, `armyPlinth` is the strip below
 * the ground line the count plaque sits in, and `wmCell*` has to be big enough
 * that `fitSpriteBox` picks a half rather than a third for a 192px war
 * machine. All three were wrong at some point and none of them fails a
 * compile, so `hero-plate.spec.ts` divides the real manifests by them.
 */
export const HERO_GEOMETRY = Object.freeze({
  armyGround: ARMY_GROUND,
  armyGroundCompact: ARMY_GROUND_COMPACT,
  armyPlinth: ARMY_PLINTH,
  armyPlinthCompact: ARMY_PLINTH_COMPACT,
  armyCountScale: ARMY_COUNT_SCALE,
  armyCountScaleCompact: ARMY_COUNT_SCALE_COMPACT,
  armySocketW: ARMY_SOCKET_W,
  armySocketWCompact: ARMY_SOCKET_W_COMPACT,
  /** Socket heights, mirrored from the two rules below that set them. */
  armySocket: 170,
  armySocketCompact: 108,
  /** The doll lattice, which decides every artifact's drawn size. */
  dollCell: DOLL_CELL,
  dollGap: DOLL_GAP,
  dollCellCompact: DOLL_CELL_COMPACT,
  dollCols: 5,
  dollRows: 6,
  /** Rows of INK in a bitmap glyph. The keyline is opt-in and drawn outside
   *  the body, so it is no longer part of the line box; this was 9 while it
   *  was, and every proportion computed from it was two rows too tall. */
  glyphRows: 7,
  masteryRungs: MASTERY_RAIL.length,
  /** The spell index's icon at 1366x768, which is what sets a row's HEIGHT
   *  there - the name is centred against it. Exported so the wrap test can
   *  hold the two-line name against the height it has to fit inside. */
  shelfIconCompact: SHELF_ICON_COMPACT,
  spellNameFontCompact: SPELL_NAME_FONT_COMPACT,
  spellNameLineCompact: SPELL_NAME_LINE_COMPACT,
  spellNameMaxLinesCompact: SPELL_NAME_MAX_LINES_COMPACT,
});

/** Exported so `hero-compact.spec.ts` can assert the 1366x768 budget. */
export const HERO_CSS = `
/* ---- the sheet is a DIALOG, the reference's own shape.
 *
 * The classic's hero screen is a carved frame about 1090px wide at 1080p with
 * the sheet inside it, standing on whatever the player was looking at. It is
 * not a screen-wide grid of cards, and the previous layout - a 352px column
 * beside a three-column working half that ran to both edges of a 1920px
 * window - was the composition a blind read named first. So: one frame,
 * centred, two columns over an army band, and a leather surround around it.
 *
 * The widths are arithmetic. The gear column needs 5 x 76 + 4 x 26 = 484 of
 * lattice plus 2 x 16 of plate and 2 x 12 of card, which is 540. The sheet
 * column holds two skill rows of a 96px medallion beside two words, and 588
 * gives each 288 with the gutters. The army band under both is 1116 wide
 * inside the card, which puts seven sockets at 135 after the button column;
 * \`hero-plate.spec.ts\` holds the widest count against that width. */
.hm-hs__screen {
  background-color: #2a1a0d;
  background-image:
    radial-gradient(120% 90% at 50% 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.62) 100%),
    ${WOOD_FIGURE},
    ${WOOD_FIBRE},
    linear-gradient(180deg, #4a3220 0%, #3a2716 55%, #24170b 100%);
  background-size: auto, 1024px 512px, 331px 331px, auto;
  background-repeat: no-repeat, repeat, repeat, no-repeat;
}
.hm-hs__screen .hs-screen__body {
  align-items: center;
  justify-content: center;
  padding: 10px;
}
.hs-hero {
  /* Held in DEVICE pixels under a browser zoomed out (the adventure column's
     rule, see SIDEBAR_CSS): the captions on these plates hold the window's
     cap floor at 80% - fifteen rows on a 1080p monitor - and a column that
     shrank to four fifths clipped "Knowledge" and "Earth Magic". */
  grid-template-columns: max(588px, calc(588px / var(--hm-dpr))) max(540px, calc(540px / var(--hm-dpr)));
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 12px;
  padding: 14px;
  width: auto;
  height: 100%;
  max-width: 100%;
  align-items: stretch;
}
.hs-hero__col { grid-column: 1; grid-row: 1; }
.hs-hero__main {
  grid-column: 2;
  grid-row: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
}
.hs-hero__gear { grid-column: 1; grid-row: 1; min-height: 0; overflow: hidden; }
.hs-hero__armycard { grid-column: 1 / -1; grid-row: 2; min-width: 0; }
.hm-hs__sheet { min-height: 0; overflow: hidden; }

/* ---- the perimeter: a black keyline, a struck gold bead, a bevelled leather
   moulding lit from the top left, and four corner rivets drawn in the
   moulding's own background so they sit ON it. */
.hs-hero {
  position: relative;
  isolation: isolate;
  padding: 22px;
  background-color: #1b1109;
  background-image:
    radial-gradient(circle at 11px 11px,
      #f7e6bb 0 2px, #b48c3e 2px 4px, rgba(0,0,0,0.9) 4px 5.5px, rgba(0,0,0,0) 5.5px),
    radial-gradient(circle at calc(100% - 11px) 11px,
      #f7e6bb 0 2px, #b48c3e 2px 4px, rgba(0,0,0,0.9) 4px 5.5px, rgba(0,0,0,0) 5.5px),
    radial-gradient(circle at 11px calc(100% - 11px),
      #f7e6bb 0 2px, #b48c3e 2px 4px, rgba(0,0,0,0.9) 4px 5.5px, rgba(0,0,0,0) 5.5px),
    radial-gradient(circle at calc(100% - 11px) calc(100% - 11px),
      #f7e6bb 0 2px, #b48c3e 2px 4px, rgba(0,0,0,0.9) 4px 5.5px, rgba(0,0,0,0) 5.5px),
    ${WOOD_FIBRE},
    linear-gradient(139deg,
      #7d5c2c 0%, #62471f 22%, #4a3416 48%, #33230e 74%, #221706 100%);
  background-size: auto, auto, auto, auto, 331px 331px, auto;
  background-repeat: no-repeat, no-repeat, no-repeat, no-repeat, repeat, no-repeat;
  /* THE RAIL IS A BAND, NOT A HAIRLINE. The classic closes its hero sheet with
     a gilt moulding five or six pixels wide - a black keyline, a cream lip, a
     two-pixel gold face, its own shade and a groove - and the sheet's leather
     starts inside that. Measured on the expansion hero sheet at 1180x664, the top
     rail is 6px from the first black row to the first leather row; ours was a
     1px gold hairline between two black lines, which read as a web border. */
  box-shadow:
    inset 0 0 0 1px #0a0704,
    inset 0 0 0 2px #f2dba4,
    inset 0 0 0 4px #c39a3a,
    inset 0 0 0 5px #6b4d15,
    inset 0 0 0 6px #0a0704,
    inset 0 7px 0 6px rgba(255,232,186,0.14),
    inset 0 -7px 0 6px rgba(0,0,0,0.55),
    inset 0 0 60px rgba(0,0,0,0.55),
    0 12px 40px rgba(0,0,0,0.7);
}
/* THE OWNER'S VELVET, between the outer moulding and the sheet. The classic wraps
   its hero sheet in the owning player's colour - blue on the expansion sheet, red
   on Edgewater's console - and it was the one cue every blind round of the
   last critique found first. A band ten pixels wide inside the six-pixel
   moulding, under the four rivets, drawn BEHIND the sheet's cards (z-index -1
   inside the element's own stacking context) so a card standing on it reads
   as a card on cloth. A town nobody owns keeps the leather. */
.hs-hero::before {
  content: "";
  position: absolute;
  inset: 6px;
  z-index: -1;
  pointer-events: none;
  background-color: var(--hs-velvet, #4a3416);
  background-image:
    repeating-linear-gradient(93deg, rgba(255,255,255,0.04) 0 1px, rgba(0,0,0,0) 1px 3px),
    linear-gradient(180deg, var(--hs-velvet-hi, #62471f) 0%, var(--hs-velvet, #4a3416) 50%, var(--hs-velvet-lo, #33230e) 100%);
  box-shadow: inset 0 0 22px rgba(0,0,0,0.45);
}
/* The inner keyline where the velvet meets the sheet. */
.hs-hero::after {
  content: "";
  position: absolute;
  inset: 18px;
  pointer-events: none;
  z-index: 0;
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.9),
    0 0 0 2px rgba(168,128,31,0.42),
    inset 0 1px 0 rgba(0,0,0,0.7);
}

/* ---- the hero's own plate: one field, the readings laid on it in cells.
   Every cell is a shallow cut with a gilt hairline, which is the reference's
   own grid - it rules its left panel into cells and prints one reading in
   each, and there are no group boxes and no headings anywhere on it. */
.hm-hs__sheet {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  padding: 10px 12px 11px;
}
.hm-hs__cell {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 5px 8px;
${reliefDecls("recess", "wood", 1)}
}
.hm-hs__celltext { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1 1 auto; }
.hm-hs__cellcap { display: flex; color: #e8d3a0; line-height: 0; }

/* The head: face at the left, the name and the level beside it, the way the
   reference sets its own. The portrait is a whole divisor of its 256px source
   (128 roomy, 64 compact) in the shared carved frame. */
.hm-hs__head { display: flex; align-items: center; gap: 14px; flex: 0 0 auto; }
.hm-hs__face { --hs-portrait-frame: 7px; margin: 0; align-self: center; flex: 0 0 auto; }
.hm-hs__title { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1 1 auto; }
.hs-hero .hs-hero__name { display: flex; margin-top: 0; color: #f6e3ae; line-height: 0; text-shadow: none; }
.hs-hero .hs-hero__class { display: flex; color: #f4e6c2; line-height: 0; text-shadow: none; }
.hs-hero .hs-hero__name svg, .hs-hero .hs-hero__class svg { display: block; }
/* #c9ab72 measured 4.8:1 on the plate at 1366x768@80% (85,56,31); #dcc08a is 6.1 there and 6.6 at 1080p. */
.hm-hs__move { display: flex; gap: 10px; margin-top: 2px; color: #dcc08a; line-height: 0; }

/* ---- primaries: four cells in a row, word over medallion over figure. */
.hs-hero .hs-primaries { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; flex: 0 0 auto; }
.hs-hero .hs-prim {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 2px 5px;
  min-width: 0;
${reliefDecls("recess", "wood", 1)}
}
.hs-hero .hs-prim__label { display: flex; line-height: 0; color: #e8d3a0; text-shadow: none; }
/* The figure on a narrow bar under the device, the reference's own row: a
   dark band the width of the cell with a gilt hairline top and bottom. */
.hs-hero .hs-prim__value {
  display: flex; justify-content: center; align-self: stretch; line-height: 0;
  padding: 3px 0 2px; margin: 1px 2px 0;
  color: #fff3d2; text-shadow: none;
  background-image: linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.22) 100%);
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.7), inset 0 -1px 0 rgba(0,0,0,0.7), 0 1px 0 rgba(255,226,170,0.12), 0 -1px 0 rgba(255,226,170,0.12);
}
/* The device sits on the cell's own sky, not a black recess: the ui.* art
   is opaque with a dark ground, and a 48px opaque tile on a black pocket
   read as a stamp. The pocket is the reference's leather. */
.hs-hero .hs-prim__socket { padding: 2px; }

/* ---- specialty, morale, luck: the reference's row */
.hm-hs__fortune { display: flex; align-items: stretch; gap: 6px; flex: 0 0 auto; }
.hm-hs__fortune .hs-special { flex: 1 1 auto; min-width: 0; }
.hs-hero .hs-special__name {
  font-size: max(16px, var(--hm-type-floor));
  line-height: 1.1;
  white-space: nowrap;
  /* Stamped in the bitmap serif since 2026-09-04, so the box is the ink and a
     plain clip can shave a row - see \`.hm-hs__skilltext\`. */
  overflow: clip;
  overflow-clip-margin: 2px;
  text-overflow: ellipsis;
}
.hs-hero .hs-special__name svg { display: block; }
.hm-hs__mood { flex: 0 0 auto; gap: 8px; padding-right: 12px; }
.hm-hs__moodnum { display: flex; color: #fff3d2; line-height: 0; }

/* ---- experience beside spell points */
.hm-hs__ledger { display: grid; grid-template-columns: 1.15fr 1fr; gap: 6px; flex: 0 0 auto; }
.hm-hs__dial { min-width: 0; }
.hm-hs__dialnum { display: flex; color: #fff3d2; line-height: 0; }
.hs-hero .hm-hs__xpbar { height: 6px; margin-top: 2px; }
.hs-hero .hm-hs__xpbar .hs-bar__fill { inset: 1px auto 1px 1px; }

/* ---- secondary skills: eight rows, two to a line, medallion beside the
   rank over the name. The rules text and the mastery rail live in the panel
   a row opens. */
.hs-hero .hs-skills {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
  gap: 6px;
  flex: 1 1 auto;
  min-height: 0;
}
.hs-skill {
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 0;
  padding: 4px 8px;
  text-align: left;
  width: 100%;
  min-height: 0;
  border: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
${reliefDecls("recess", "wood", 1)}
}
.hm-hs__skillrow {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
}
.hm-hs__skillbtn:hover:not(:disabled) { filter: brightness(1.18); }
.hm-hs__skillbtn:focus-visible { outline: 2px solid #d9b25a; outline-offset: -2px; }
.hm-hs__skillbtn:disabled { cursor: default; }
.hs-hero .hs-skill__socket { align-self: center; padding: 2px; flex: 0 0 auto; }
.hm-hs__skilltext {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  /* \`clip\` with a margin, not \`hidden\`: this box is exactly the ink of the
     two bitmap readings in it, and under a fractional device ratio a plain
     clip shaves the row crispEdges snapped just past the box. The margin
     costs no layout. */
  overflow: clip;
  overflow-clip-margin: 2px;
}
.hs-hero .hs-skill__level { display: flex; line-height: 0; color: #e8d3a0; text-shadow: none; letter-spacing: 0; }
.hs-hero .hs-skill__name { display: flex; line-height: 0; font-size: 0; color: #fff3d2; text-shadow: none; white-space: normal; overflow: visible; }

/* The three-rung mastery rail and the clamped rules text the plate used to
   carry. Neither is drawn on the sheet any more - the reference's skill row is
   a medallion and two words, and the panel a row opens prints all three tiers
   - but the rules are the contract \`hero-plate.spec.ts\`, \`hero-compact.spec.ts\`
   and \`skill-detail.spec.ts\` pin, and the well's arithmetic is what the panel
   would need if the text ever came back to the plate. */
.hm-hs__track {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3px;
  flex: 0 0 auto;
}
.hm-hs__rung {
  padding: 3px 0 2px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(9px, var(--hm-type-floor));
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-align: center;
  color: #6d5c3e;
  background-image: linear-gradient(180deg, rgba(0,0,0,0.42), rgba(0,0,0,0.16));
  text-shadow: 0 1px 0 rgba(0,0,0,0.7);
}
.hm-hs__rung--lit {
  color: #22170a;
  background-image: linear-gradient(180deg, #f0d79a 0%, #cda75a 46%, #9a7527 100%);
}
.hm-hs__skillgain { display: flex; align-items: baseline; flex-wrap: wrap; gap: 2px 8px; min-width: 0; flex: 0 0 auto; }
.hm-hs__skillnote {
  margin-top: 3px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(${String(SKILL_NOTE_FONT)}px, var(--hm-type-floor));
  line-height: ${String(SKILL_NOTE_LINE_EM)};
  color: #cbb289;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}
.hm-hs__skillnotein {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${String(SKILL_NOTE_MAX_LINES)};
  max-height: ${String(SKILL_NOTE_MAX_LINES * SKILL_NOTE_LINE_EM)}em;
  overflow: hidden;
}

/* ---- the skill panel: all three tiers, with what each is worth */
.hm-hs__skillpanel { width: min(560px, 92vw); gap: 0; min-height: 0; }
.hm-hs__skillhead { display: flex; align-items: center; gap: 14px; margin-bottom: 10px; flex: 0 0 auto; }
.hm-hs__skillplate { flex: 0 0 auto; }
.hm-hs__skillhead-text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.hm-hs__skillhead .hs-dialog__title { margin-bottom: 0; font-size: max(20px, var(--hm-type-floor)); }
.hm-hs__skillrank {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.06em;
  color: #c0a271;
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
.hm-hs__tiers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.hm-hs__skillclose { flex: 0 0 auto; }
.hm-hs__tier {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 11px 9px;
${reliefDecls("recess", "stone", 1)}
}
.hm-hs__tier--now {
${reliefDecls("chamfer", "wood", 2)}
  outline: 1px solid rgba(217,178,90,0.55);
  outline-offset: -3px;
}
.hm-hs__tiername {
  display: flex;
  align-items: baseline;
  gap: 9px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.19em;
  text-transform: uppercase;
  color: #ecc87f;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}
.hm-hs__tiernow {
  font-size: max(9px, var(--hm-type-floor));
  letter-spacing: 0.14em;
  color: #241a0e;
  padding: 1px 6px;
  background-image: linear-gradient(180deg, #e6c47c 0%, #b98f34 100%);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.55);
}
.hm-hs__tiertext {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(13.5px, var(--hm-type-floor));
  line-height: 1.45;
  color: #e5d3ab;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
}
.hm-hs__tierstats { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
.hm-hs__chip {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.03em;
  color: #fff0c6;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
  padding: 1px 8px 2px;
${reliefDecls("chamfer", "bronze", 1)}
}
button.hm-hs__chip {
  margin: 0;
  border: 0;
  cursor: pointer;
  text-align: left;
}
button.hm-hs__chip--open {
  outline: 1px solid #d9b25a;
  outline-offset: 0;
  filter: brightness(1.12);
}
.hm-hs__subdesc {
  flex: 1 1 100%;
  margin: 2px 0 0;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  line-height: 1.4;
  color: #e5d3ab;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
}
.hm-hs__subhint { margin: 6px 0 0; }
.hm-hs__tiernone {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.04em;
  color: #9c855c;
}
.hm-hs__skillfoot { margin-top: 10px; line-height: 1.45; }

/* ---- the spell index this sheet no longer draws. The classic's hero screen
   carries no list of spells - the book is a slot on the doll and a button on
   the foot - and the index was the widest of the three columns the old
   layout spread across the window. The rules stay because the compact-sheet
   contract (\`hero-compact.spec.ts\`) reads them, and so a shelf can be put
   back on a screen that has room for one. */
.hs-shelf {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: space-between;
  gap: 2px 12px;
  padding: 9px 10px;
}
.hm-hs__band { display: contents; }
.hm-hs__bandrule {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 3px;
  margin-bottom: 2px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(10px, var(--hm-type-floor));
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #d8b571;
}
.hm-hs__spellrow { display: flex; align-items: center; gap: 6px; min-width: 0; padding: 1px 0; }
.hm-hs__spellname {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(13px, var(--hm-type-floor));
  color: #ecd9ae;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hs-hero .hs-shelf__icon { padding: 1px; }

/* ---- the equipment plate: one leather board with a knight on it.
 *
 * The lattice is five columns by six rows from 'DOLL', the socket is 76 and
 * the gutter 26, which puts 19 x 76^2 into 484 x 586 - 38.7% socket against
 * the reference's 36.6%. The gutter is what the figure underneath is seen
 * through. */
.hs-hero__gear { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; padding: 10px 12px 11px; }
.hm-hs__plate {
  position: relative;
  flex: 1 1 auto;
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-height: 0;
  width: 100%;
  padding: 14px 16px 10px;
  background-image:
    radial-gradient(88% 46% at 50% 0%, rgba(255,220,170,0.09) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(70% 50% at 50% 100%, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0) 62%),
    ${WOOD_FIBRE},
    linear-gradient(172deg, #5e4326 0%, #4a3319 46%, #2f200e 100%);
  background-size: auto, auto, 331px 331px, auto;
  background-repeat: no-repeat, no-repeat, repeat, no-repeat;
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px rgba(168,128,31,0.45),
    inset 0 3px 9px rgba(0,0,0,0.5),
    inset 0 -14px 26px rgba(0,0,0,0.30),
    0 1px 0 rgba(255,226,170,0.14);
}
.hm-hs__figure {
  position: relative;
  flex: 0 0 auto;
  display: grid;
  box-sizing: content-box;
  grid-template-columns: repeat(5, ${String(DOLL_CELL)}px);
  grid-template-rows: repeat(6, ${String(DOLL_CELL)}px);
  gap: ${String(DOLL_GAP)}px;
}
/* The figure itself: a FILL in the owner's red, keylined only on the
   silhouette, under the sockets - so the head socket has a helm in it and the
   boots socket a hem, which is why an empty doll still reads as a person. */
.hm-hs__body {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.hm-hs__body polygon {
  fill: rgba(178,44,38,0.40);
  stroke: rgba(233,199,124,0.62);
  stroke-width: 0.7;
  stroke-linejoin: round;
}
/* ...and in the OWNER'S colour when the sheet knows one: the figure is the
   cloth the sockets are cut through, and the classic paints it in the player's
   colour (blue on the expansion sheet). The neutral red above is the fallback. */
.hm-hs__screen .hm-hs__figure .hm-hs__body polygon { fill: var(--hs-figure, rgba(178,44,38,0.40)); }
.hs-hero .hm-hs__figure .hs-slot {
  position: relative;
  width: ${String(DOLL_CELL)}px; height: ${String(DOLL_CELL)}px;
  max-width: none;
  aspect-ratio: auto;
}
/* An empty cut is translucent, so the figure is what is at the bottom of it. */
.hs-hero .hm-hs__figure .hs-slot--empty {
  background-color: rgba(26,16,7,0.30);
  background-image:
    linear-gradient(180deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.17) 62%, rgba(0,0,0,0.05) 100%);
  box-shadow:
    inset 0 2px 0 0 rgba(0,0,0,0.55),
    inset 0 -2px 0 0 rgba(255,226,170,0.06),
    inset 2px 0 0 0 rgba(0,0,0,0.35),
    inset -2px 0 0 0 rgba(0,0,0,0.35),
    inset 0 4px 8px rgba(0,0,0,0.42);
}
/* The pack: a strip across the foot of the SAME board under one scored line. */
.hm-hs__packrule {
  flex: 0 0 auto;
  align-self: stretch;
  height: 2px;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0) 0%, rgba(226,186,104,0.45) 14%,
    rgba(226,186,104,0.45) 86%, rgba(226,186,104,0) 100%);
  box-shadow: 0 1px 0 rgba(0,0,0,0.55);
}
.hm-hs__packwell {
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* The strip proper: what is left after the combination shelf, measured by
   \`packShelf\` for its row count, and paged rather than scrolled when a
   pack outruns it. */
/* Two rows of pockets, always.
   The strip divides whatever height the gear card has left, and on a short
   window that was 38px - a scroller the height of half a pocket, which holds
   every relic past the fifth and cannot be dragged by a finger. A floor of two
   rows costs a roomy sheet nothing (the desktop strip is three rows tall
   already) and is the difference between a hidden pack and a usable one on
   every touch screen that is not classed as a phone. */
.hm-hs__packgrid {
  flex: 1 1 0;
  min-height: ${String(PACK_CELL * 2 + PACK_GAP)}px;
  overflow: hidden;
}
.hs-hero--compact .hm-hs__packgrid {
  min-height: ${String(PACK_CELL_COMPACT * 2 + PACK_GAP_COMPACT)}px;
}
.hs-backpack {
  grid-template-columns: repeat(${String(PACK_COLS)}, minmax(0, ${String(PACK_CELL)}px));
  justify-content: center;
  gap: ${String(PACK_GAP)}px;
}
.hs-hero .hs-backpack .hs-slot { max-width: ${String(PACK_CELL)}px; }
.hs-hero .hs-backpack .hs-slot--empty {
  box-shadow:
    inset 0 2px 0 0 rgba(0,0,0,0.55),
    inset 0 -2px 0 0 rgba(255,226,170,0.06),
    inset 2px 0 0 0 rgba(0,0,0,0.35),
    inset -2px 0 0 0 rgba(0,0,0,0.35),
    inset 0 4px 8px rgba(0,0,0,0.42);
}

/* ---- the army: one ground line under seven stacks, the buttons at the end */
.hs-hero .hs-hero__armycard { padding: 8px 12px 7px; }
.hs-hero .hs-hero__muster { display: flex; align-items: stretch; gap: 10px; }
.hs-hero__muster .hm-hs__armyrow { flex: 1 1 auto; min-width: 0; }
.hs-hero .hs-armyrow__head { display: none; }
/* ...except in a meeting, where the two rows are two DIFFERENT heroes' armies
   sitting side by side on one line, and which seven are whose is the one thing
   the player has to know before he drags anything. The classic stacks the two heroes
   vertically and can leave both rows unlabelled; this sheet has one figure and
   one bag, so it labels instead. A garrison keeps the bare row: there the
   second army is the town the sheet is already inside. */
.hs-hero--meeting .hs-hero .hs-armyrow__head {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  margin-bottom: 3px;
  color: var(--hs-ink-dim, #d8c9a4);
  font-size: max(13px, var(--hm-type-floor));
  letter-spacing: 0.02em;
  opacity: 0.85;
}
/* The gutter between the two armies, so the fourteen sockets read as seven and
   seven rather than as one long shelf. */
.hs-hero--meeting .hs-hero .hs-hero__muster { gap: 22px; }
.hs-hero--meeting .hs-hero__muster .hm-hs__armyrow + .hm-hs__armyrow {
  border-left: 1px solid rgba(0,0,0,0.45);
  box-shadow: inset 1px 0 0 rgba(255,255,255,0.08);
  padding-left: 22px;
}
/* The creature card: a stat table and an ability list beside the sprite. */
.hs-hero .hm-hs__unitart { width: 132px; height: 132px; flex: 0 0 auto; display: flex; align-items: center; justify-content: center; }
.hs-hero .hm-hs__unitstats { border-collapse: collapse; }
.hs-hero .hm-hs__unitstats th { text-align: left; font-weight: normal; padding: 1px 12px 1px 0; }
.hs-hero .hm-hs__unitstats td { text-align: right; padding: 1px 0; }
.hs-hero .hm-hs__unitabilities { margin: 0; padding-left: 16px; }
.hs-hero .hm-hs__armyrow .hs-armyrow__slots {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.hs-hero .hm-hs__armyrow .hs-slot.hm-hs__army {
  position: relative;
  width: auto;
  height: 170px;
  overflow: hidden;
}
.hm-hs__armyart { position: absolute; left: 50%; transform: translateX(-50%); image-rendering: pixelated; }
.hm-hs__armyshadow {
  position: absolute;
  left: 50%;
  bottom: ${String(ARMY_PLINTH - 4)}px;
  width: 74%;
  height: 16px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.28) 58%, rgba(0,0,0,0) 100%);
}
.hs-hero .hm-hs__plinth {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: ${String(ARMY_PLINTH)}px;
  pointer-events: none;
  background-image:
    linear-gradient(180deg, rgba(72,54,28,0.94) 0%, rgba(38,27,13,0.96) 55%, rgba(16,11,5,0.98) 100%);
  box-shadow:
    inset 0 1px 0 rgba(255,226,176,0.20),
    inset 0 2px 5px rgba(0,0,0,0.55);
}
.hs-hero .hm-hs__armyrow .hs-slot.hm-hs__army::after {
  content: "";
  position: absolute;
  left: 6%; right: 6%;
  bottom: ${String(ARMY_PLINTH)}px;
  height: 1px;
  background: linear-gradient(90deg,
    rgba(255,226,176,0) 0%, rgba(255,226,176,0.34) 30%, rgba(255,226,176,0.34) 70%, rgba(255,226,176,0) 100%);
}
/* The count: outlined figures on the creature, the way the reference draws
   them, and not a plate. See ARMY_COUNT_SCALE. */
.hs-hero .hm-hs__army .hs-slot__count {
  right: 5px;
  bottom: ${String(Math.floor(ARMY_PLINTH / 2) - 3)}px;
  min-width: 0;
  padding: 0;
  line-height: 0;
  display: flex;
  align-items: flex-end;
  color: #fdf3d8;
  background-image: none;
  box-shadow: none;
}
.hs-hero .hm-hs__figure .hs-slot:not(.hs-slot--empty) {
  background-color: #241f22;
  background-image:
    radial-gradient(78% 62% at 50% 108%, rgba(150,178,206,0.16) 0%, rgba(0,0,0,0) 62%),
    linear-gradient(180deg, #1b171b 0%, #262127 58%, #2f2830 100%);
}
.hs-hero .hm-hs__figure .hs-slot .hs-slot__art {
  filter: drop-shadow(0 0 1px rgba(255,236,196,0.45)) drop-shadow(0 2px 3px rgba(0,0,0,0.7));
}
/* The two buttons, stacked at the band's right end. */
.hm-hs__buttons {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  width: ${String(BUTTONS_W)}px;
  /* Never narrower than its widest label: SPELLBOOK at the 1080p cap floor
     is 174 CSS px at 80% zoom against a 156px column, and the column grows
     by the difference rather than the label overrunning the plate. */
  min-width: max-content;
}
/* 4px of side padding, not 8: SPELLBOOK at the 1080p cap floor is 8px wider
   than the plate at 80% zoom with 8, and exactly the plate with 4. */
.hs-hero .hm-hs__button { padding: 9px 4px; gap: 0; font-size: 0; line-height: 0; }
.hs-hero .hm-hs__button::before, .hs-hero .hm-hs__button::after { display: none; }
.hs-hero .hm-hs__button:disabled { cursor: default; filter: saturate(0.4) brightness(0.7); }

/* ---- 1366x768: the same sheet, smaller sprites and tighter mouldings.
   Nothing is dropped. The gear column needs 5 x 64 + 4 x 6 = 344 of lattice
   plus 2 x 8 of plate and 2 x 8 of card, which is 376; the sheet takes 408. */
/* Held in DEVICE px under a zoomed-out page, as the roomy grid is: the
   bitmap captions are floored in device px, and at 80% on 1366x768 the
   11-row Knowledge label (87 device px) overhung its 74.8 px primaries cell on
   both sides. 408 / 0.8 CSS px is the same 408 device px the sheet has at
   100%, where the label stands with 3 px of air. */
.hs-hero--compact .hs-hero { grid-template-columns: max(408px, calc(408px / var(--hm-dpr))) max(376px, calc(376px / var(--hm-dpr))); gap: 8px; padding: 13px; }
.hs-hero--compact .hs-hero::before { inset: 5px; }
.hs-hero--compact .hs-hero::after { inset: 11px; }
.hs-hero--compact .hs-hero__main { grid-template-columns: minmax(0, 1fr); width: 376px; gap: 8px; }
/* The same 376 held in DEVICE px under a zoomed-out page (see the grid rule
   above). A rule of its own so the 1366px budget \`hero-compact.spec.ts\`
   reads off the plain rule stays a list of whole pixels. */
.hs-hero--compact .hs-hero .hs-hero__main { width: max(376px, calc(376px / var(--hm-dpr))); }
.hs-hero--compact .hs-hero__col, .hs-hero--compact .hs-hero__gear { gap: 8px; }
.hs-hero--compact .hs-hero .hs-card { padding: 7px 8px 7px; }
.hs-hero--compact .hm-hs__sheet { gap: 5px; }
.hs-hero--compact .hm-hs__head { gap: 10px; }
.hs-hero--compact .hm-hs__face { --hs-portrait-frame: 5px; }
.hs-hero--compact .hm-hs__cell { padding: 3px 6px; gap: 6px; }
.hs-hero--compact .hs-hero .hs-prim { padding: 4px 2px 3px; gap: 2px; }
.hs-hero--compact .hs-hero .hs-special__name { font-size: max(13px, var(--hm-type-floor)); }
.hs-hero--compact .hs-hero .hs-skills { gap: 4px; }
.hs-hero--compact .hs-skill { padding: 2px 5px; }
.hs-hero--compact .hm-hs__skillrow { gap: 7px; }
.hs-hero--compact .hm-hs__skilltext { gap: 2px; }
.hs-hero--compact .hs-hero .hs-skill__socket { padding: 1px; }
.hs-hero--compact .hm-hs__plate { padding: 8px 8px 7px; gap: 6px; }
.hs-hero--compact .hm-hs__figure {
  grid-template-columns: repeat(5, ${String(DOLL_CELL_COMPACT)}px);
  grid-template-rows: repeat(6, ${String(DOLL_CELL_COMPACT)}px);
  column-gap: ${String(DOLL_GAP_X_COMPACT)}px;
  row-gap: ${String(DOLL_GAP_Y_COMPACT)}px;
}
.hs-hero--compact .hs-hero .hm-hs__figure .hs-slot,
.hs-hero--compact .hm-hs__figure .hs-slot {
  width: ${String(DOLL_CELL_COMPACT)}px; height: ${String(DOLL_CELL_COMPACT)}px;
}
.hs-hero--compact .hm-hs__body polygon { stroke-width: 0.9; }
.hs-hero--compact .hs-hero .hs-hero__armycard { padding: 6px 8px 5px; }
.hs-hero--compact .hs-hero .hm-hs__armyrow .hs-slot.hm-hs__army { height: 108px; }
.hs-hero--compact .hm-hs__armyshadow { bottom: ${String(ARMY_PLINTH_COMPACT - 4)}px; height: 11px; }
.hs-hero--compact .hs-hero .hm-hs__armyrow .hs-slot.hm-hs__army::after {
  bottom: ${String(ARMY_PLINTH_COMPACT)}px;
}
.hs-hero--compact .hs-hero .hm-hs__plinth { height: ${String(ARMY_PLINTH_COMPACT)}px; }
.hs-hero--compact .hs-hero .hm-hs__army .hs-slot__count {
  right: 3px;
  bottom: ${String(Math.floor(ARMY_PLINTH_COMPACT / 2) - 2)}px;
}
.hs-hero--compact .hm-hs__buttons { width: ${String(BUTTONS_W_COMPACT)}px; gap: 5px; }
.hs-hero--compact .hs-hero .hm-hs__button { padding: 6px 4px; }
/* The rail and the rules text at 1366x768, on the same contract as above. */
.hs-hero--compact .hm-hs__track { gap: 2px; }
.hs-hero--compact .hm-hs__rung {
  font-size: max(8.5px, var(--hm-type-floor));
  letter-spacing: 0.03em;
  padding: 2px 0 1px;
  white-space: nowrap;
}
.hs-hero--compact .hm-hs__skillgain { display: none; }
.hs-hero--compact .hm-hs__skillnote {
  font-size: max(${String(SKILL_NOTE_FONT_COMPACT)}px, var(--hm-type-floor));
  margin-top: 0;
}
.hs-hero--compact .hm-hs__skillnotein { -webkit-line-clamp: ${String(SKILL_NOTE_MAX_LINES)}; }
.hs-hero--compact .hm-hs__skillpanel { width: min(500px, 92vw); }
.hs-hero--compact .hm-hs__tier { padding: 6px 9px 7px; }
.hs-hero--compact .hm-hs__tiertext { font-size: max(12.5px, var(--hm-type-floor)); line-height: 1.4; }
/* The spell index at 1366x768, on the same contract. */
.hs-hero--compact .hs-hero .hs-shelf__icon { padding: 0; }
.hs-hero--compact .hm-hs__bandrule { line-height: 1; margin-bottom: 0; padding-bottom: 2px; }
.hs-hero--compact .hm-hs__spellname {
  font-size: max(${String(SPELL_NAME_FONT_COMPACT)}px, var(--hm-type-floor));
  white-space: normal;
  line-height: ${String(SPELL_NAME_LINE_COMPACT)};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${String(SPELL_NAME_MAX_LINES_COMPACT)};
  max-height: ${String(SPELL_NAME_MAX_LINES_COMPACT * SPELL_NAME_LINE_COMPACT)}em;
}
.hs-hero--compact .hs-shelf { gap: 0 12px; padding: 3px 6px 2px; }

/* Landscape phone: the compact sheet is authored for ~768 rows. A ~390-row
   phone cannot show that plate at 1:1. Scale the whole carved frame to the
   window (sprites, bitmap type and all) so the player sees the sheet at once
   instead of scrolling a desktop layout. */
.hs-hero--phone { --hs-phone-scale: 0.5; overflow: hidden; }
.hm-root--phone .hs-hero--phone { overflow: hidden; }
/* The body SCROLLS on a portrait phone.
   A hero sheet is about 1,150 x 1,100 of desktop plate. Shrinking it until its
   height fits an 844-row window puts every gear socket under 20px and nobody
   can equip anything with a thumb; so the width is fitted (phoneSheetScale in
   its width mode) and the rest is reached by scrolling - which is how the
   doll's own sockets and the backpack under them became reachable at all. It
   is the y axis only: the width has been fitted, so a sideways scroll could
   only ever move the sheet off the screen. */
.hm-root--phone .hs-hero--phone .hs-screen__body,
.hs-hero--phone .hs-screen__body {
  display: flex;
  align-items: flex-start;
  /* Centred when it fits, top-aligned when it does not: safe is what stops a
     centred flex item that overflows from hanging off the TOP of the scroller
     where it can never be scrolled back to. A browser that does not know the
     keyword keeps the line above. */
  align-items: safe center;
  justify-content: center;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  padding: 4px;
}
.hs-hero--phone .hs-hero {
  height: auto;
  max-height: none;
  overflow: visible;
  /* max-content, so the box is the PLATE and not whatever the phone window
     gave it. Measured as a flex child it reported the window's own width, the
     fit concluded "it fits", and the right third of the sheet was drawn
     outside the screen with no way to reach it. */
  width: max-content;
  /* and no ceiling on it: the plate's own max-width: 100% is what pinned the
     box to the phone window while its content spilled out of it. */
  max-width: none;
  flex: 0 0 auto;
  transform: translateX(var(--hs-phone-shift-x, 0px)) scale(var(--hs-phone-scale));
  transform-origin: top left;
  /* scale() leaves the box full size, so the scroller would run three times
     past the bottom of the drawing. phoneSheetTrim is exactly the
     difference. */
  margin-bottom: calc(-1 * var(--hs-phone-trim, 0px));
}
.hs-hero--phone .hm-hs__sheet,
.hs-hero--phone .hs-hero__gear,
.hs-hero--phone .hs-hero__col,
.hs-hero--phone .hs-hero__main {
  overflow: visible;
  min-height: min-content;
}
.hs-hero--phone .hs-hero { gap: 4px; padding: 8px; }
.hs-hero--phone .hs-hero .hs-card { padding: 4px 5px; }
.hs-hero--phone .hm-hs__sheet { gap: 3px; padding: 4px 6px; }
.hs-hero--phone .hm-hs__head { gap: 6px; }
.hs-hero--phone .hm-hs__cell { padding: 2px 4px; gap: 4px; }
.hs-hero--phone .hs-hero .hs-prim { padding: 2px 1px; gap: 1px; }
.hs-hero--phone .hs-hero .hs-skills { gap: 2px; }
.hs-hero--phone .hs-skill { padding: 1px 3px; }
.hs-hero--phone .hs-hero .hs-hero__armycard { padding: 3px 5px; }
.hs-hero--phone .hs-hero .hm-hs__armyrow .hs-slot.hm-hs__army { height: 72px; }
/* The pack strip GROWS on a phone instead of scrolling inside itself.
   The card's leftover height on a 390px window was 38px, which made the
   backpack an 18px-tall scroller holding half a row - the relics were in it
   and unreachable. The plate's own body is the scroller now, so the well and
   the strip are sized by their contents and the sheet gets taller. */
.hs-hero--phone .hm-hs__packwell,
.hs-hero--phone .hm-hs__packgrid {
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
}
.hs-hero--phone .hs-hero__gear { height: auto; align-self: start; }
/* The pager sits under the strip, which on a phone is directly above the
   SPELLBOOK plate: arrows big enough to hit, the count between them so the
   player knows there IS a second page before he goes looking for one. */
.hm-hs__packpager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
}
.hm-hs__packstep {
  /* In PLATE pixels, which a phone draws at about half: 120x96 here lands on
     the glass at roughly 57x46, and 44 is the smallest thing a thumb hits. */
  min-width: 120px;
  min-height: 96px;
  font-size: 44px;
  line-height: 1;
  color: var(--hm-gold-4);
  cursor: pointer;
}
.hm-hs__packstep:disabled { opacity: 0.35; cursor: default; }
.hm-hs__packcount { display: flex; align-items: center; color: var(--hm-text-dim); }
/* On a desktop the same pager at mouse size, stood on end BESIDE the strip:
   a thumb needs 96px of plate, a pointer needs a plate the height of a line
   of type, and a row of arrows under the strip cost the shelf a row of
   pockets (three at 1080p became two). The strip is 390px in a well half
   again as wide, so the column sits in the margin the strip never used, and
   the shelf keeps every row it has height for. */
.hs-screen:not(.hs-hero--phone) .hm-hs__packpager {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 4px;
  width: ${String(PACK_PAGER_W)}px;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  margin: 0;
}
/* The strip centres in what the column leaves, so the count never brushes the
   last pocket; a pack that fits on one shelf has no column and stays centred
   under the doll. */
.hs-screen:not(.hs-hero--phone) .hm-hs__packgrid--paged { padding-right: ${String(PACK_PAGER_W + 8)}px; }
.hs-hero--compact .hm-hs__packpager { width: ${String(PACK_PAGER_W_COMPACT)}px; }
.hs-hero--compact .hm-hs__packgrid--paged { padding-right: ${String(PACK_PAGER_W_COMPACT + 6)}px; }
.hs-screen:not(.hs-hero--phone) .hm-hs__packstep {
  min-width: 40px;
  min-height: 26px;
  padding: 0 4px;
  font-size: max(16px, var(--hm-type-floor));
}
/* The plate's diamond ornaments crowd a 40px plate; the sheet's own buttons
   drop them for the same reason. */
.hs-screen:not(.hs-hero--phone) .hm-hs__packstep::before,
.hs-screen:not(.hs-hero--phone) .hm-hs__packstep::after { display: none; }
.hs-screen:not(.hs-hero--phone) .hm-hs__packcount {
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
}
.hs-hero--compact .hm-hs__packstep {
  min-width: 32px;
  min-height: 20px;
  font-size: max(12px, var(--hm-type-floor));
}
.hs-hero--phone .hm-hs__buttons { gap: 3px; }
.hs-hero--phone .hs-hero .hm-hs__button { padding: 3px; }

/* Dialogs over a ~390-row phone: the desktop 480-560 boxes cover the sheet.
   Keep them a card, not a second screen. */
.hs-hero--phone .hs-dialog {
  max-width: min(360px, 88vw);
  max-height: min(280px, 74vh);
  padding: 8px 10px 6px;
}
.hs-hero--phone .hs-dialog__title {
  font-size: max(13px, var(--hm-type-floor));
  margin-bottom: 4px;
}
.hs-hero--phone .hs-dialog__foot {
  margin-top: 6px;
  padding-top: 6px;
}
.hs-hero--phone .hs-dialog__close { padding: 4px 12px; }
/* The wear button is the phone's whole equip gesture, so it is a target before
   it is a button: 44px is the smallest thing a thumb hits reliably, and the
   plate is drawn at about half size, so it asks for twice that. */
.hm-hs__wearbtn {
  margin-top: 10px;
  align-self: flex-start;
  min-height: 32px;
  padding: 6px 18px;
}
.hs-hero--phone .hm-hs__wearbtn { min-height: 88px; padding: 10px 28px; font-size: 28px; }
/* A stack in hand: the line that says so sits over the army rows, and every
   socket it could go to is ringed so the next tap has somewhere to aim. */
.hs-hero__armycard { position: relative; }
.hm-hs__carryhint {
  position: absolute; left: 0; right: 0; bottom: calc(100% + 4px); z-index: 3;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 6px 10px;
  background: rgba(20, 14, 8, 0.94);
  border: 1px solid var(--hm-gold-1, #6b4d15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}
.hm-hs__carryhint .hs-btn { min-height: 28px; padding: 4px 12px; flex: 0 0 auto; }
.hs-hero--phone .hm-hs__carryhint { gap: 14px; padding: 10px 16px; }
.hs-hero--phone .hm-hs__carryhint .hs-tiny { font-size: 26px; }
.hs-hero--phone .hm-hs__carryhint .hs-btn { min-height: 72px; padding: 8px 22px; font-size: 26px; }
.hs-slot--open { outline: 2px dashed var(--hm-gold-3, #d9b25a); outline-offset: -3px; cursor: pointer; }
.hs-hero--phone .hm-hs__skillpanel {
  width: min(360px, 88vw);
  height: min(340px, 88vh);
  max-height: min(340px, 88vh);
}
.hs-hero--phone .hm-hs__skillhead { gap: 6px; margin-bottom: 4px; }
.hs-hero--phone .hm-hs__skillplate { width: 24px; height: 24px; }
.hs-hero--phone .hm-hs__skillhead .hs-dialog__title {
  font-size: max(13px, var(--hm-type-floor));
}
.hs-hero--phone .hm-hs__skillrank { font-size: max(10px, var(--hm-type-floor)); }
.hs-hero--phone .hm-hs__subhint { margin: 2px 0 0; font-size: max(10px, var(--hm-type-floor)); }
.hs-hero--phone .hm-hs__tiers { gap: 5px; margin-top: 4px; }
.hs-hero--phone .hm-hs__skillpanel .hs-dialog__foot {
  margin-top: 4px;
  padding-top: 4px;
}
.hs-hero--phone .hm-hs__skillclose {
  padding: 2px 8px;
  gap: 0;
  font-size: max(10px, var(--hm-type-floor));
}
.hs-hero--phone .hm-hs__skillclose::before,
.hs-hero--phone .hm-hs__skillclose::after { display: none; }
.hs-hero--phone .hm-hs__tier { padding: 5px 7px 6px; gap: 2px; }
.hs-hero--phone .hm-hs__tiername { font-size: max(10px, var(--hm-type-floor)); gap: 6px; }
.hs-hero--phone .hm-hs__tiertext {
  font-size: max(11px, var(--hm-type-floor));
  line-height: 1.3;
}
.hs-hero--phone .hm-hs__chip { font-size: max(10px, var(--hm-type-floor)); padding: 1px 6px; }
.hs-hero--phone .hm-hs__subdesc {
  font-size: max(11px, var(--hm-type-floor));
  line-height: 1.35;
}
.hs-hero--phone .hm-hs__unitart { width: 64px; height: 64px; flex-basis: 64px; }
`;

export function useHeroStyles(): void {
  useStyleSheet(HERO_STYLE_ID, HERO_CSS, StyleLayer.screen);
}
